import { ChevronDownIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { useState } from 'react';

import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { MatterProvider } from '@/providers/MatterProvider';
import type { ReadDepartmentRecordsChannel } from '@/services/api/generated/policeReportsService';

// Helper to format phone numbers nicely
const formatPhoneNumber = (phoneNumber: string): string => {
  try {
    const parsed = parsePhoneNumberWithError(phoneNumber, 'US');
    if (parsed && parsed.isValid()) {
      return parsed.formatNational();
    }
  } catch {
    // If parsing fails, try basic formatting for US numbers
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
  }
  return phoneNumber;
};

interface CallButtonProps {
  phoneChannels: ReadDepartmentRecordsChannel[];
  departmentName: string;
  matterId: string;
}

export const CallButton = ({
  phoneChannels,
  departmentName,
  matterId,
}: CallButtonProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [callModalState, setCallModalState] = useState<{
    isOpen: boolean;
    phoneNumber: string;
    label: string;
  } | null>(null);

  if (phoneChannels.length === 0) return null;

  const handlePhoneClick = (channel: ReadDepartmentRecordsChannel) => {
    const phoneNumber = channel.handle?.value;
    if (!phoneNumber) return;

    const label = channel.title || departmentName;

    setCallModalState({
      isOpen: true,
      phoneNumber,
      label,
    });
    setShowMenu(false);
  };

  // Single phone channel - direct button
  if (phoneChannels.length === 1) {
    const channel = phoneChannels[0];
    const phoneNumber = channel.handle?.value;
    if (!phoneNumber) return null;

    return (
      <>
        <button
          type="button"
          onClick={() => handlePhoneClick(channel)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
        >
          <PhoneIcon className="w-3.5 h-3.5" />
          Call
        </button>

        {callModalState && (
          <MatterProvider matterId={matterId}>
            <MakeCallModal
              isOpen={callModalState.isOpen}
              onClose={() => setCallModalState(null)}
              initialPhoneNumber={callModalState.phoneNumber}
              initialContactLabel={callModalState.label}
              source="police_call_button"
            />
          </MatterProvider>
        )}
      </>
    );
  }

  // Multiple phone channels - dropdown
  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowMenu(!showMenu)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
        >
          <PhoneIcon className="w-3.5 h-3.5" />
          Call
          <ChevronDownIcon className="w-3 h-3" />
        </button>
        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowMenu(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowMenu(false);
              }}
              role="button"
              tabIndex={0}
              aria-label="Close menu"
            />
            <div className="absolute left-0 top-full mt-1 bg-white rounded border border-gray-200 shadow-lg z-20 min-w-[200px]">
              {phoneChannels.map((channel, idx) => {
                const phoneNumber = channel.handle?.value;
                if (!phoneNumber) return null;

                const displayName =
                  channel.title || channel.description || 'Phone';
                const formattedPhone = formatPhoneNumber(phoneNumber);

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handlePhoneClick(channel)}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 border-t border-gray-100 first:border-t-0"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">
                        {displayName}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {formattedPhone}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {callModalState && (
        <MatterProvider matterId={matterId}>
          <MakeCallModal
            isOpen={callModalState.isOpen}
            onClose={() => setCallModalState(null)}
            initialPhoneNumber={callModalState.phoneNumber}
            initialContactLabel={callModalState.label}
            source="police_call_button"
          />
        </MatterProvider>
      )}
    </>
  );
};
